import { MouseEvent, ReactNode } from 'react';
import { Icon } from '@zapier/zinnia-icons';

import styles from './Alert.module.scss';

const getIconName = (status: Status) => {
  switch (status) {
    default:
    case 'info':
      return 'alertInfoFill';
    case 'success':
      return 'formCheckCircle';
    case 'warning':
      return 'alertCircleColor';
    case 'error':
      return 'formXCircle';
  }
};

export type Status = 'info' | 'success' | 'warning' | 'error';

export type Variant = 'alert' | 'banner' | 'compact' | 'toast';

export type AlertProps = {
  /**
   * Controls the `aria-live` attribute of `Alert`.
   */
  'aria-live'?: 'off' | 'polite' | 'assertive';
  /**
   * The content to render within `Alert`. Typically comprised of
   * subcomponents like `AlertDescription` and `ButtonGroup`.
   */
  children?: ReactNode;
  /**
   * Optional function to call when `Alert` is dismissed. Its presence
   * determines whether the close button renders.
   */
  onDismiss?: (event: MouseEvent) => void;
  /**
   * Controls the `role` attribute of `Alert`.
   */
  role?: 'alert' | 'alertdialog';
  /**
   * Controls the styling of `Alert` to indicate to users what type of alert it
   * is.
   * @default "info"
   */
  status?: Status;
  /**
   * The title of the `Alert`.
   */
  title?: ReactNode;
  /**
   * Controls how content within `Alert` is laid out.
   *
   * The default Alert (Variant Alert) appears in-line in the main content, most
   * of the time they’re positioned horizontally centered.
   *
   * Compact Alert should be used as in-line notification where density is
   * higher than usual.
   *
   * Toast should be used as a block notification over the page.
   *
   * Banner should be used as a block notification on top of the page or a
   * section.
   *
   * @default 'alert'
   */
  variant?: Variant;
};

/**
 * An Alert informs users of important and occasionally time-sensitive changes.
 */
export const Alert = ({
  children = undefined,
  onDismiss = undefined,
  role = undefined,
  status = 'info',
  title = undefined,
  variant = 'alert',
  'aria-live': ariaLive,
}: AlertProps) => {
  return (
    <article
      aria-live={ariaLive}
      className={styles.root}
      data-status={status}
      role={role}
      data-variant={variant}
      data-zds
      data-has-title={title ? 'true' : undefined}
    >
      <div
        className={styles.statusIcon}
        data-status={status}
        data-variant={variant}
        data-zds
        data-has-title={title ? 'true' : undefined}
      >
        <Icon
          aria-label={status}
          isBlock={true}
          name={getIconName(status)}
          size={20}
        />
      </div>
      <div className={styles.container} data-zds>
        {title && (
          <h2 className={styles.title} data-variant={variant} data-zds>
            {title}
          </h2>
        )}
        {children}
      </div>
      {onDismiss && (
        <button
          type="button"
          aria-label="Dismiss"
          className={styles.closeButton}
          onClick={onDismiss}
          data-variant={variant}
          data-zds
        >
          <Icon isBlock={true} name="formX" size={20} />
        </button>
      )}
    </article>
  );
};
